import type { JSX } from 'react';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { Editor } from '@tiptap/react';
import {
  BoldOutlined,
  CodeOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  PictureOutlined,
  RedoOutlined,
  StrikethroughOutlined,
  TableOutlined,
  UnderlineOutlined,
  UndoOutlined,
  UnorderedListOutlined,
  LoadingOutlined,
  VideoCameraOutlined,
  ThunderboltOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { Popover, Tooltip } from 'antd';

import { EmojiPicker } from '../components/EmojiPicker';
import type { SlashUploadFn } from './slash-commands';
import { mediaEmbedAttrs } from './media-embed';

// ─── HUB 2.0 (W2): AI-кнопки «Переписать проще» / «Проверить на противоречия» ──
// TiptapEditor (общий для статей, уроков, новостей) мы не трогаем: страница
// статьи регистрирует обработчики здесь, и тулбар показывает кнопки только
// пока они зарегистрированы (в остальных редакторах их нет).
export interface ToolbarAiActions {
  rewrite?: () => void;
  checkConflicts?: () => void;
}
let toolbarAi: ToolbarAiActions | null = null;
const toolbarAiListeners = new Set<() => void>();
/** Зарегистрировать AI-действия тулбара; возвращает функцию снятия. */
export function setToolbarAiActions(actions: ToolbarAiActions | null): () => void {
  toolbarAi = actions;
  toolbarAiListeners.forEach((l) => l());
  return () => {
    if (toolbarAi === actions) {
      toolbarAi = null;
      toolbarAiListeners.forEach((l) => l());
    }
  };
}
function subscribeToolbarAi(cb: () => void): () => void {
  toolbarAiListeners.add(cb);
  return () => {
    toolbarAiListeners.delete(cb);
  };
}
function useToolbarAiActions(): ToolbarAiActions | null {
  return useSyncExternalStore(subscribeToolbarAi, () => toolbarAi);
}

const aiBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 4,
  border: '1px solid rgba(255,105,0,.35)', background: 'var(--fk-orange-soft, rgba(255,105,0,.06))',
  color: 'var(--fk-orange, #FF6900)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
};

/**
 * Toolbar редактора — sticky под H1 заголовком. Минимально-необходимый
 * набор для статей: H1/H2/H3, B/I/U/S, списки, ссылка, картинка, код,
 * таблица. Расширенное форматирование (цвет, фон, выравнивание) — через
 * bubble-меню при выделении.
 *
 * В отличие от Teamly где toolbar плавает поверх контента сверху —
 * у нас он закреплён в начале статьи (более читаемо).
 */
export function TiptapToolbar({ editor, uploadFile }: { editor: Editor; uploadFile?: SlashUploadFn }): JSX.Element {
  const ai = useToolbarAiActions();
  const action = (run: () => void) => () => {
    editor.chain().focus();
    run();
  };

  const addLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL ссылки', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  // Вставляем пустой блок-плейсхолдер изображения (выбор источника — в блоке).
  const addImage = () => {
    editor.chain().focus().insertContent(mediaEmbedAttrs('image')).run();
  };

  return (
    <div
      className="tiptap-toolbar"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        padding: '8px 12px',
        borderBottom: '1px solid var(--border)',
        background: '#fff',
        position: 'sticky',
        top: 0,
        zIndex: 5,
      }}
    >
      <ToolGroup>
        <ToolBtn title="Отмена (Ctrl+Z)" onClick={action(() => editor.chain().undo().run())}>
          <UndoOutlined />
        </ToolBtn>
        <ToolBtn title="Повтор (Ctrl+Y)" onClick={action(() => editor.chain().redo().run())}>
          <RedoOutlined />
        </ToolBtn>
      </ToolGroup>

      <ToolDivider />

      <ToolGroup>
        <HeadingPicker editor={editor} />
      </ToolGroup>

      <ToolDivider />

      <ToolGroup>
        <ToolBtn
          title="Жирный (Ctrl+B)"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <BoldOutlined />
        </ToolBtn>
        <ToolBtn
          title="Курсив (Ctrl+I)"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <ItalicOutlined />
        </ToolBtn>
        <ToolBtn
          title="Подчёркивание (Ctrl+U)"
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineOutlined />
        </ToolBtn>
        <ToolBtn
          title="Зачёркивание"
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <StrikethroughOutlined />
        </ToolBtn>
        <ToolBtn
          title="Код"
          active={editor.isActive('code')}
          onClick={() => editor.chain().focus().toggleCode().run()}
        >
          <CodeOutlined />
        </ToolBtn>
      </ToolGroup>

      <ToolDivider />

      <ToolGroup>
        <ToolBtn
          title="Маркированный список"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <UnorderedListOutlined />
        </ToolBtn>
        <ToolBtn
          title="Нумерованный список"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <OrderedListOutlined />
        </ToolBtn>
        <ToolBtn
          title="Чек-лист"
          active={editor.isActive('taskList')}
          onClick={() => editor.chain().focus().toggleTaskList().run()}
        >
          ☑
        </ToolBtn>
      </ToolGroup>

      <ToolDivider />

      <ToolGroup>
        <ToolBtn title="Ссылка" active={editor.isActive('link')} onClick={addLink}>
          <LinkOutlined />
        </ToolBtn>
        <ToolBtn title="Картинка" onClick={addImage}>
          <PictureOutlined />
        </ToolBtn>
        <ToolBtn
          title="Таблица"
          onClick={() =>
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          }
        >
          <TableOutlined />
        </ToolBtn>
        <EmojiButton editor={editor} />
        {uploadFile && <ScreenRecordButton editor={editor} uploadFile={uploadFile} />}
      </ToolGroup>

      {ai && (ai.rewrite || ai.checkConflicts) && (
        <>
          <ToolDivider />
          <ToolGroup>
            {ai.rewrite && (
              <button type="button" style={aiBtnStyle} onClick={ai.rewrite} title="ИИ поправит статью по вашей просьбе или по стайлгайду и покажет разницу">
                <ThunderboltOutlined /> ИИ: править
              </button>
            )}
            {ai.checkConflicts && (
              <button type="button" style={{ ...aiBtnStyle, marginLeft: 4 }} onClick={ai.checkConflicts} title="AI сверит стандарты одной категории и найдёт противоречия">
                <SafetyCertificateOutlined /> Проверить на противоречия
              </button>
            )}
          </ToolGroup>
        </>
      )}

      <div style={{ flex: 1 }} />
      <div
        style={{
          fontSize: 11,
          color: 'var(--text-muted)',
          alignSelf: 'center',
          paddingRight: 6,
        }}
      >
        Введите <kbd style={kbdStyle}>/</kbd> для меню блоков
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────

function ToolBtn({
  title,
  active,
  onClick,
  children,
}: {
  title: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Tooltip title={title}>
      <button
        type="button"
        onClick={onClick}
        style={{
          background: active ? 'rgba(255, 105, 0, 0.12)' : 'transparent',
          color: active ? 'var(--brasol-accent)' : 'var(--text-secondary)',
          border: 'none',
          padding: '6px 8px',
          cursor: 'pointer',
          borderRadius: 6,
          fontSize: 14,
          minWidth: 28,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'inherit',
        }}
        onMouseEnter={(e) => {
          if (!active) {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,0,0,0.05)';
          }
        }}
        onMouseLeave={(e) => {
          if (!active) {
            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
          }
        }}
      >
        {children}
      </button>
    </Tooltip>
  );
}

/**
 * «Записать экран» — короткое видео прямо в статью, без сторонних программ.
 * Пишем getDisplayMedia + MediaRecorder в webm, по остановке грузим файл как
 * обычное вложение статьи и вставляем блок видео.
 *
 * Кнопки нет, если браузер не умеет захват экрана (мобильные Safari/Chrome) —
 * лучше не показывать то, что не сработает.
 */
function ScreenRecordButton({ editor, uploadFile }: { editor: Editor; uploadFile: SlashUploadFn }): JSX.Element | null {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recRef = useRef<MediaRecorder | null>(null);

  useEffect(() => {
    if (!recording) return;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [recording]);

  const supported = typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getDisplayMedia
    && typeof MediaRecorder !== 'undefined';
  if (!supported) return null;

  const start = async (): Promise<void> => {
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
    } catch {
      return; // пользователь отменил выбор окна — это не ошибка
    }
    const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm';
    const rec = new MediaRecorder(stream, { mimeType: mime });
    const chunks: BlobPart[] = [];
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
    rec.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      setRecording(false);
      const blob = new Blob(chunks, { type: 'video/webm' });
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
      const file = new File([blob], `zapis-ekrana-${stamp}.webm`, { type: 'video/webm' });
      setUploading(true);
      void uploadFile(file)
        .then((r) => {
          if (r?.url) {
            editor.chain().focus().insertContent({ type: 'mediaEmbed', attrs: { kind: 'video', src: r.url, name: r.fileName } }).run();
          }
        })
        .finally(() => setUploading(false));
    };
    // Останов и по кнопке браузера «Прекратить доступ», и по нашей.
    stream.getVideoTracks()[0]?.addEventListener('ended', () => { if (rec.state !== 'inactive') rec.stop(); });
    recRef.current = rec;
    setSeconds(0);
    setRecording(true);
    rec.start(1000);
  };

  const stop = (): void => {
    if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
  };

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  return (
    <Tooltip title={recording ? 'Идёт запись экрана — нажмите, чтобы остановить' : 'Записать экран в статью'}>
      <button
        type="button"
        onClick={() => (recording ? stop() : void start())}
        disabled={uploading}
        style={{
          background: recording ? 'rgba(229,57,53,.12)' : 'transparent',
          color: recording ? 'var(--fk-error)' : 'var(--text-secondary)',
          border: 'none', padding: '6px 8px', cursor: uploading ? 'wait' : 'pointer', borderRadius: 6,
          fontSize: 14, minWidth: 28, display: 'inline-flex', alignItems: 'center', gap: 6,
          fontFamily: 'inherit', fontWeight: recording ? 700 : 400,
        }}
      >
        {uploading ? <LoadingOutlined spin /> : recording ? <span style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--fk-error)' }} /> : <VideoCameraOutlined />}
        {recording ? mmss : null}
      </button>
    </Tooltip>
  );
}

/** Кнопка-пикер эмодзи в тулбаре: общий EmojiPicker (полный набор + поиск). */
function EmojiButton({ editor }: { editor: Editor }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <Popover
      content={
        <EmojiPicker
          onPick={(e) => { editor.chain().focus().insertContent(e).run(); setOpen(false); }}
        />
      }
      trigger="click"
      placement="bottomLeft"
      styles={{ container: { padding: 10 } }}
      open={open}
      onOpenChange={setOpen}
    >
      <button
        type="button"
        title="Эмодзи"
        style={{
          background: 'transparent', color: 'var(--text-secondary)', border: 'none',
          padding: '6px 8px', cursor: 'pointer', borderRadius: 6, fontSize: 15, minWidth: 28,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit',
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,0,0,0.05)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
      >
        😀
      </button>
    </Popover>
  );
}

function ToolGroup({ children }: { children: React.ReactNode }): JSX.Element {
  return <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>{children}</div>;
}

function ToolDivider(): JSX.Element {
  return (
    <div
      style={{
        width: 1,
        height: 22,
        background: 'var(--border)',
        margin: '0 2px',
        alignSelf: 'center',
      }}
    />
  );
}

function HeadingPicker({ editor }: { editor: Editor }): JSX.Element {
  const current = editor.isActive('heading', { level: 1 })
    ? 'h1'
    : editor.isActive('heading', { level: 2 })
      ? 'h2'
      : editor.isActive('heading', { level: 3 })
        ? 'h3'
        : editor.isActive('heading', { level: 4 })
          ? 'h4'
          : 'p';
  return (
    <select
      value={current}
      onChange={(e) => {
        const v = e.target.value;
        if (v === 'p') {
          editor.chain().focus().setParagraph().run();
        } else {
          const level = parseInt(v.slice(1), 10) as 1 | 2 | 3 | 4;
          editor.chain().focus().toggleHeading({ level }).run();
        }
      }}
      style={{
        background: 'transparent',
        border: '1px solid var(--border)',
        borderRadius: 6,
        padding: '4px 8px',
        fontSize: 13,
        color: 'var(--text-secondary)',
        cursor: 'pointer',
      }}
    >
      <option value="p">Текст</option>
      <option value="h1">Заголовок 1</option>
      <option value="h2">Заголовок 2</option>
      <option value="h3">Заголовок 3</option>
      <option value="h4">Заголовок 4</option>
    </select>
  );
}

const kbdStyle: React.CSSProperties = {
  padding: '1px 5px',
  background: 'var(--bg)',
  border: '1px solid var(--border)',
  borderRadius: 4,
  fontFamily: 'monospace',
  fontSize: 11,
};
